import { ampli } from 'lib-shared-tracking';
import {
  type ConversationSummaryInfoSummary,
  type ReadOrGenerateSummaryApiArg,
} from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMount } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useTranslateText } from 'utils/useTranslateText';

import { ConversationContext } from '../conversationProvider';
import {
  ConversationSummary,
  ConversationSummaryFeedbackOptions,
  ConversationSummaryProps,
} from '../conversationSummary';

export type ConversationSummaryByCsidProps = {
  /** The conversation ID. */
  csid: number;
  /** An event triggered when the summary is loaded. */
  onSummaryLoaded?: (summary: ConversationSummaryInfoSummary | null) => void;
  /**
   * Whether the summary is stale. Undefined when the summary is not rendered
   * alongside messages.
   */
  isStale?: boolean;
} & PropsWithClassName &
  Pick<ReadOrGenerateSummaryApiArg, 'view'> &
  Pick<ConversationSummaryProps, 'scrollIntoViewOnMount'>;

/** Represents the component for displaying summary of a conversation. */
export const ConversationSummaryByCsid = memo(
  function ConversationSummaryByCsid({
    csid,
    view,
    onSummaryLoaded,
    isStale,
    className,
    scrollIntoViewOnMount,
  }: ConversationSummaryByCsidProps): JSX.Element | null {
    const { hasCopilot } = useCopilotTrial();
    const {
      isSummaryVisible: contextIsSummaryVisible,
      conversation,
      summaryLanguageCode: wantedSummaryLanguage,
    } = useContext(ConversationContext);
    const conversationLanguage = conversation?.attributes?.language ?? null;
    const [fetchSummary, { data: summaryData, isFetching, isError }] =
      conversationsApi.useLazyReadOrGenerateSummaryQuery();
    const [summaryError, setSummaryError] = useState({});
    const [translate, { asyncStatus }] = useTranslateText();
    const [summary, setSummary] =
      useState<ConversationSummaryInfoSummary | null>(
        conversation?.summary?.current ?? null,
      );

    const handleFeedback = useCallback(
      ({ isUseful }: ConversationSummaryFeedbackOptions) => {
        if (!summary?.id) {
          return;
        }

        if (isUseful) {
          ampli.conversationSummaryFeedbackUseful({
            summaryId: summary.id,
          });
        } else {
          ampli.conversationSummaryFeedbackNotUseful({
            summaryId: summary.id,
          });
        }
      },
      [summary?.id],
    );

    const shouldRenderSummary = useMemo(() => {
      if (!hasCopilot) {
        return false;
      }

      return view !== 'details' || contextIsSummaryVisible;
    }, [hasCopilot, view, contextIsSummaryVisible]);

    const shouldRegenerateSummary = shouldRenderSummary && !summary;
    const currentSummaryLanguage = summary?.language ?? conversationLanguage;
    const summaryNeedsTranslation =
      shouldRenderSummary &&
      summary &&
      wantedSummaryLanguage &&
      currentSummaryLanguage !== wantedSummaryLanguage;

    const feedbackData = useMemo(
      () => ({
        csid,
        view,
        summary,
      }),
      [csid, summary, view],
    );

    const isLoading =
      isFetching || asyncStatus === 'pending' || (!summary && !isError);

    const fetchSummaryAndEmitLoaded = useCallback(
      (arg: ReadOrGenerateSummaryApiArg) => {
        return fetchSummary(arg).then((response) => {
          if (response.error) {
            setSummaryError(response.error);
          }

          if (response.data) {
            onSummaryLoaded?.(response.data);
            setSummary(response.data);
            setSummaryError({});
          }
          return response;
        });
      },
      [fetchSummary, onSummaryLoaded],
    );

    const translateSummary = useCallback(
      async (summary: string, language: typeof wantedSummaryLanguage) => {
        if (!language) {
          return summary;
        }

        const translation = await translate({
          text: summary,
          language,
        });
        return translation?.translatedText ?? summary;
      },
      [translate],
    );

    const translateSummaryIfNecessary = useCallback(() => {
      if (summaryNeedsTranslation) {
        translateSummary(summary?.summary, wantedSummaryLanguage).then(
          (translated) => {
            const translatedSummary = {
              ...summary,
              summary: translated,
              language: wantedSummaryLanguage,
            };
            onSummaryLoaded?.(translatedSummary);
            setSummary(translatedSummary);
          },
        );
      }
    }, [
      summaryNeedsTranslation,
      translateSummary,
      summary,
      wantedSummaryLanguage,
      onSummaryLoaded,
    ]);

    const initNecessary = useCallback(() => {
      if (shouldRegenerateSummary) {
        fetchSummaryAndEmitLoaded({ csid, view }).then(() => {
          translateSummaryIfNecessary();
        });
      }
    }, [
      csid,
      fetchSummaryAndEmitLoaded,
      shouldRegenerateSummary,
      translateSummaryIfNecessary,
      view,
    ]);

    const handleRequestRegenerate = useCallback(() => {
      fetchSummaryAndEmitLoaded({
        csid,
        view,
        regenerate: true,
      });
      if (summaryData?.id) {
        ampli.conversationSummaryRegenerateSummary({
          summaryId: summaryData.id,
        });
      }
    }, [fetchSummaryAndEmitLoaded, csid, view, summaryData]);

    useEffect(translateSummaryIfNecessary, [
      summaryNeedsTranslation,
      wantedSummaryLanguage,
      translateSummaryIfNecessary,
    ]);

    useMount(initNecessary);

    if (!shouldRenderSummary) {
      return null;
    }

    return (
      <ConversationSummary
        className={className}
        isFetching={isLoading}
        isStale={isStale}
        summary={summary}
        feedbackData={feedbackData}
        onFeedback={handleFeedback}
        onRequestRegenerate={handleRequestRegenerate}
        scrollIntoViewOnMount={scrollIntoViewOnMount}
        error={Object.keys(summaryError).length > 0 ? summaryError : undefined}
      />
    );
  },
);
